import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Trash2, Copy, Eye, EyeOff, Settings,
} from 'lucide-react';
import { getBlock, type BlockInstance } from '@sytel/blocks';
import { usePageEditor } from './usePageEditor';
import { cn } from '@/lib/cn';

export function BlockList() {
  const { blocks, selectedBlockId, selectBlock, removeBlock, duplicateBlock, toggleBlockVisible } = usePageEditor();

  if (blocks.length === 0) {
    return <EmptyDropzone />;
  }

  return (
    <ol className="space-y-1.5">
      {/* Drop zone at the very top */}
      <InsertionDropzone index={0} />
      {blocks.map((block, idx) => (
        <div key={block.id}>
          <SortableBlockItem
            block={block}
            selected={block.id === selectedBlockId}
            onClick={() => selectBlock(block.id)}
            onRemove={() => removeBlock(block.id)}
            onDuplicate={() => duplicateBlock(block.id)}
            onToggleVisible={() => toggleBlockVisible(block.id)}
          />
          <InsertionDropzone index={idx + 1} />
        </div>
      ))}
    </ol>
  );
}

function EmptyDropzone() {
  const { setNodeRef, isOver } = useDroppable({ id: 'dropzone-empty' });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-[280px] items-center justify-center rounded-xl border-2 border-dashed p-12 text-center text-sm transition-colors',
        isOver
          ? 'border-neutral-900 bg-neutral-900/5 text-neutral-900'
          : 'border-neutral-300 bg-white/50 text-neutral-500',
      )}
    >
      <div>
        <div className="text-base font-medium text-neutral-700">Empieza arrastrando un bloque aquí</div>
        <div className="mt-1 text-xs text-neutral-500">
          O haz click en cualquier bloque del panel izquierdo.
        </div>
      </div>
    </div>
  );
}

function InsertionDropzone({ index }: { index: number }) {
  const { setNodeRef, isOver, active } = useDroppable({
    id: `dropzone-${index}`,
    data: { index },
  });
  const isDragging = active != null;
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'pointer-events-auto transition-all',
        isDragging ? 'h-6' : 'h-0 overflow-hidden',
        isOver && 'h-12',
      )}
    >
      <div
        className={cn(
          'mx-auto h-full rounded transition-all',
          isOver
            ? 'border-2 border-dashed border-neutral-900 bg-neutral-900/5'
            : 'border border-dashed border-neutral-300 bg-transparent',
        )}
      />
    </div>
  );
}

interface SortableBlockItemProps {
  block: BlockInstance;
  selected: boolean;
  onClick: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onToggleVisible: () => void;
}

function SortableBlockItem({
  block, selected, onClick, onRemove, onDuplicate, onToggleVisible,
}: SortableBlockItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    data: { source: 'list', blockId: block.id },
  });
  const def = getBlock(block.type);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-2 rounded-lg border bg-white p-3 transition-all',
        selected
          ? 'border-neutral-900 ring-2 ring-neutral-900/10 shadow-sm'
          : 'border-neutral-200 hover:border-neutral-300 hover:shadow-sm',
        !block.visible && 'opacity-60',
      )}
    >
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 active:cursor-grabbing"
        aria-label="Arrastrar"
      >
        <GripVertical className="size-4" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{def?.meta.label ?? block.type}</span>
          {!block.visible && (
            <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-neutral-500">
              Oculto
            </span>
          )}
        </div>
        <div className="truncate text-xs text-neutral-500">
          {previewLabel(block.data as Record<string, unknown>) || def?.meta.description}
        </div>
      </div>
      <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <IconBtn
          label={block.visible ? 'Ocultar' : 'Mostrar'}
          onClick={(e) => { e.stopPropagation(); onToggleVisible(); }}
        >
          {block.visible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
        </IconBtn>
        <IconBtn label="Duplicar" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
          <Copy className="size-3.5" />
        </IconBtn>
        <IconBtn label="Editar" onClick={(e) => { e.stopPropagation(); onClick(); }}>
          <Settings className="size-3.5" />
        </IconBtn>
        <IconBtn
          label="Eliminar"
          variant="danger"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm('¿Eliminar este bloque?')) onRemove();
          }}
        >
          <Trash2 className="size-3.5" />
        </IconBtn>
      </div>
    </li>
  );
}

function IconBtn({
  children, onClick, label, variant = 'default',
}: {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  label: string;
  variant?: 'default' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'rounded p-1 transition-colors',
        variant === 'danger'
          ? 'text-neutral-400 hover:bg-red-50 hover:text-red-600'
          : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700',
      )}
    >
      {children}
    </button>
  );
}

function previewLabel(data: Record<string, unknown>): string {
  const candidates = ['h1', 'h1Top', 'title', 'question', 'quote', 'name', 'eyebrow', 'label'];
  for (const key of candidates) {
    const value = data[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return '';
}
